import React, { createContext, useContext, useMemo, useCallback, ReactNode } from 'react';
import {
  OverlayAdaptors,
  VideoOverlayAdaptor,
  ImageOverlayAdaptor,
  SoundOverlayAdaptor,
  TextOverlayAdaptor,
  StickerOverlayAdaptor,
  TemplateOverlayAdaptor,
  AnimationOverlayAdaptor,
  MergedSearchResult,
} from '../types/overlay-adaptors';
import { MediaSearchParams, StandardVideo, StandardImage, StandardAudio } from '../types/media-adaptors';
import { TextOverlayTemplate } from '../templates/text-overlay-templates';
import { StickerTemplateConfig, StickerCategory } from '../types/sticker-templates';
import { TemplateOverlay } from '../types';
import { getDefaultAudioAdaptors } from '../adaptors/default-audio-adaptors';
import { getDefaultTextAdaptors } from '../adaptors/default-text-adaptors';
import { getDefaultTemplateAdaptors } from '../adaptors/default-templates-adaptor';
import { getDefaultAnimationAdaptors, AnimationTemplate } from '../adaptors/default-animation-adaptors';
import { searchAcrossAdaptors, getTemplatesAcrossAdaptors } from './utils/adaptor-search-utils';

interface MediaAdaptorContextType {
  // Raw adaptors
  videoAdaptors: VideoOverlayAdaptor[];
  imageAdaptors: ImageOverlayAdaptor[];
  audioAdaptors: SoundOverlayAdaptor[];
  textAdaptors: TextOverlayAdaptor[];
  stickerAdaptors: StickerOverlayAdaptor[];
  templateAdaptors: TemplateOverlayAdaptor[];
  animationAdaptors: AnimationOverlayAdaptor[];

  // Merged search functions
  searchVideos: (params: MediaSearchParams, config?: Record<string, any>) => Promise<MergedSearchResult<StandardVideo>>;
  searchImages: (params: MediaSearchParams, config?: Record<string, any>) => Promise<MergedSearchResult<StandardImage>>;
  searchAudio: (params: { query?: string; page?: number; perPage?: number; }, config?: Record<string, any>) => Promise<MergedSearchResult<StandardAudio>>;
  getTextTemplates: (config?: Record<string, any>) => Promise<MergedSearchResult<TextOverlayTemplate>>;
  getStickerTemplates: (category?: StickerCategory, config?: Record<string, any>) => Promise<MergedSearchResult<StickerTemplateConfig>>;
  getTemplateOverlays: (params?: { searchQuery?: string; page?: number; perPage?: number; }, config?: Record<string, any>) => Promise<MergedSearchResult<TemplateOverlay>>;
  getAnimationTemplates: (config?: Record<string, any>) => Promise<MergedSearchResult<AnimationTemplate>>;
}

const MediaAdaptorContext = createContext<MediaAdaptorContextType | null>(null);

interface MediaAdaptorProviderProps {
  children: ReactNode;
  adaptors?: OverlayAdaptors;
}

export const MediaAdaptorProvider: React.FC<MediaAdaptorProviderProps> = ({
  children,
  adaptors
}) => {
  // Resolve adaptors with defaults
  const resolvedAdaptors = useMemo(() => ({
    videoAdaptors: adaptors?.video || [],
    imageAdaptors: adaptors?.images || [],
    audioAdaptors: adaptors?.audio || getDefaultAudioAdaptors(),
    textAdaptors: adaptors?.text || getDefaultTextAdaptors(),
    stickerAdaptors: adaptors?.stickers || [],
    templateAdaptors: adaptors?.templates || getDefaultTemplateAdaptors(),
    animationAdaptors: adaptors?.animations || getDefaultAnimationAdaptors(),
  }), [adaptors]);

  // Video search across all video adaptors
  const searchVideos = useCallback(async (
    params: MediaSearchParams,
    config?: Record<string, any>
  ): Promise<MergedSearchResult<StandardVideo>> => {
    return searchAcrossAdaptors(
      resolvedAdaptors.videoAdaptors,
      params,
      config,
      { shuffle: true, errorMessage: 'Error searching videos' }
    );
  }, [resolvedAdaptors.videoAdaptors]);

  // Image search across all image adaptors
  const searchImages = useCallback(async (
    params: MediaSearchParams,
    config?: Record<string, any>
  ): Promise<MergedSearchResult<StandardImage>> => {
    return searchAcrossAdaptors(
      resolvedAdaptors.imageAdaptors,
      params,
      config,
      { shuffle: true, errorMessage: 'Error searching images' }
    );
  }, [resolvedAdaptors.imageAdaptors]);

  // Audio search across all audio adaptors
  const searchAudio = useCallback(async (
    params: { query?: string; page?: number; perPage?: number; },
    config?: Record<string, any>
  ): Promise<MergedSearchResult<StandardAudio>> => {
    return searchAcrossAdaptors(
      resolvedAdaptors.audioAdaptors,
      params,
      config,
      { shuffle: true, errorMessage: 'Error searching audio' }
    );
  }, [resolvedAdaptors.audioAdaptors]);

  // Text templates across all text adaptors
  const getTextTemplates = useCallback(async (
    config?: Record<string, any>
  ): Promise<MergedSearchResult<TextOverlayTemplate>> => {
    return getTemplatesAcrossAdaptors(
      resolvedAdaptors.textAdaptors,
      undefined,
      config,
      { shuffle: false, errorMessage: 'Error fetching text templates', enablePagination: false }
    );
  }, [resolvedAdaptors.textAdaptors]);

  const getStickerTemplates = useCallback(async (
  ): Promise<MergedSearchResult<StickerTemplateConfig>> => {
    // TODO: Implement in stickers step
    return { items: [], totalCount: 0, hasMore: false, sourceResults: [] };
  }, []);

  // Animation templates across all animation adaptors
  const getAnimationTemplates = useCallback(async (
    config?: Record<string, any>
  ): Promise<MergedSearchResult<AnimationTemplate>> => {
    return getTemplatesAcrossAdaptors(
      resolvedAdaptors.animationAdaptors,
      undefined,
      config,
      { shuffle: true, errorMessage: 'Error fetching animation templates', enablePagination: false }
    );
  }, [resolvedAdaptors.animationAdaptors]);

  // Template overlays across all template adaptors
  const getTemplateOverlays = useCallback(async (
    params: { searchQuery?: string; page?: number; perPage?: number; } = {},
    config?: Record<string, any>
  ): Promise<MergedSearchResult<TemplateOverlay>> => {
    console.log('getTemplateOverlays called with params:', params);
    console.log('Active template adaptors:', resolvedAdaptors.templateAdaptors);

    const result = await getTemplatesAcrossAdaptors(
      resolvedAdaptors.templateAdaptors,
      params,
      config,
      { shuffle: true, errorMessage: 'Error fetching templates', enablePagination: true }
    );

    console.log('Final merged items:', result.items);
    return result;
  }, [resolvedAdaptors.templateAdaptors]);

  const contextValue = useMemo(() => ({
    videoAdaptors: resolvedAdaptors.videoAdaptors,
    imageAdaptors: resolvedAdaptors.imageAdaptors,
    audioAdaptors: resolvedAdaptors.audioAdaptors,
    textAdaptors: resolvedAdaptors.textAdaptors,
    stickerAdaptors: resolvedAdaptors.stickerAdaptors,
    templateAdaptors: resolvedAdaptors.templateAdaptors,
    animationAdaptors: resolvedAdaptors.animationAdaptors,
    searchVideos,
    searchImages,
    searchAudio,
    getTextTemplates,
    getStickerTemplates,
    getTemplateOverlays,
    getAnimationTemplates,
  }), [resolvedAdaptors, searchVideos, searchImages, searchAudio, getTextTemplates, getStickerTemplates, getTemplateOverlays, getAnimationTemplates]);

  return (
    <MediaAdaptorContext.Provider value={contextValue}>
      {children}
    </MediaAdaptorContext.Provider>
  );
};

export const useMediaAdaptors = (): MediaAdaptorContextType => {
  const context = useContext(MediaAdaptorContext);
  if (!context) {
    throw new Error('useMediaAdaptors must be used within MediaAdaptorProvider');
  }
  return context;
};
